import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ExternalLink, Github, ArrowRight, Star } from 'lucide-react';

interface ProjectProps {
  title: string;
  description: string;
  image: string;
  technologies: string[];
  githubUrl: string;
  liveUrl?: string;
  delay: number;
  featured?: boolean;
}

const ProjectCard: React.FC<ProjectProps> = ({
  title, description, image, technologies, githubUrl, liveUrl, delay, featured = false,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8 }}
      className="group relative rounded-3xl overflow-hidden cursor-pointer h-full flex flex-col"
      style={{
        background: 'rgba(255,255,255,0.7)',
        border: '1px solid #ffffff',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.85)';
        (e.currentTarget as HTMLElement).style.borderColor = '#ffffff';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 48px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.7)';
        (e.currentTarget as HTMLElement).style.borderColor = '#ffffff';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.05)';
      }}
    >
      {/* Featured badge */}
      {featured && (
        <div
          className="absolute top-4 left-4 z-20 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
          style={{
            background: 'rgba(14,165,233,0.1)',
            border: '1px solid rgba(14,165,233,0.2)',
            color: '#0f172a',
            fontFamily: '"Outfit", sans-serif',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          <Star size={12} className="text-slate-700" />
          Featured
        </div>
      )}

      {/* Image area */}
      <div className="relative overflow-hidden h-52 sm:h-64">
        <motion.img
          src={image}
          alt={title}
          className="w-full h-full object-cover"
          style={{ filter: 'brightness(0.8) saturate(0.9)' }}
          whileHover={{ scale: 1.05, filter: 'brightness(1) saturate(1)' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
        {/* Soft gradient overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
          }}
        />

        {/* Tech badges on hover */}
        <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
          {technologies.slice(0, 4).map((tech, i) => (
            <span
              key={i}
              className="px-3 py-1 rounded-full text-[11px] font-medium"
              style={{
                background: 'rgba(14,165,233,0.1)',
                border: '1px solid rgba(14,165,233,0.2)',
                color: '#0f172a',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                fontFamily: '"Outfit", sans-serif',
              }}
            >
              {tech}
            </span>
          ))}
          {technologies.length > 4 && (
            <span
              className="px-3 py-1 rounded-full text-[11px] font-medium"
              style={{
                background: 'rgba(14,165,233,0.1)',
                border: '1px solid rgba(14,165,233,0.2)',
                color: '#0f172a',
                fontFamily: '"Outfit", sans-serif',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
              }}
            >
              +{technologies.length - 4}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-6 sm:p-8">
        {/* Tech tags (always visible) */}
        <div className="flex flex-wrap gap-2 mb-5 group-hover:opacity-0 transition-opacity duration-300">
          {technologies.slice(0, 3).map((tech, i) => (
            <span
              key={i}
              className="px-3 py-1 rounded-full text-[11px] font-medium"
              style={{
                background: 'rgba(14,165,233,0.1)',
                border: '1px solid rgba(14,165,233,0.2)',
                color: '#475569',
                fontFamily: '"Outfit", sans-serif',
              }}
            >
              {tech}
            </span>
          ))}
        </div>

        <h3
          className="text-xl sm:text-2xl font-semibold mb-3 transition-colors duration-300"
          style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', color: '#0f172a', letterSpacing: '-0.02em' }}
        >
          {title}
        </h3>

        <p
          className="text-sm sm:text-base leading-relaxed mb-6 flex-1 line-clamp-3"
          style={{ color: '#475569', fontFamily: '"Outfit", sans-serif' }}
        >
          {description}
        </p>

        {/* Buttons */}
        <div className="flex items-center gap-3 mt-auto pt-2">
          <motion.a
            href={liveUrl && liveUrl !== '#' ? liveUrl : githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-medium transition-all duration-300"
            style={{
              background: 'rgba(14,165,233,0.1)',
              border: '1px solid rgba(14,165,233,0.2)',
              color: '#0f172a',
              fontFamily: '"Outfit", sans-serif',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(14,165,233,0.2)';
              (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(14,165,233,0.1)';
              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
            }}
          >
            {liveUrl && liveUrl !== '#' ? (
              <><ExternalLink size={16} /><span>Live Preview</span></>
            ) : (
              <><Github size={16} /><span>View Code</span></>
            )}
          </motion.a>

          {liveUrl && liveUrl !== '#' && (
            <motion.a
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-12 h-12 flex items-center justify-center rounded-2xl transition-all duration-300"
              style={{
                background: 'rgba(14,165,233,0.1)',
                border: '1px solid rgba(14,165,233,0.2)',
                color: '#475569',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.color = '#0f172a';
                (e.currentTarget as HTMLElement).style.background = 'rgba(14,165,233,0.2)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(14,165,233,0.3)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.color = '#475569';
                (e.currentTarget as HTMLElement).style.background = 'rgba(14,165,233,0.1)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(14,165,233,0.2)';
              }}
              aria-label="GitHub"
            >
              <Github size={18} />
            </motion.a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Projects: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const projects = [
    {
      title: 'ParthaEducare',
      description: 'A comprehensive medical education consultancy website based in Ranaghat, West Bengal. Connects students with medical and pharmacy educational opportunities across India, offering expert career guidance and modern web design.',
      image: `${process.env.PUBLIC_URL}/assets/images/Projects/parthaeducare.png`,
      technologies: ['HTML5', 'CSS3', 'JavaScript ES6+', 'Responsive Design'],
      githubUrl: 'https://github.com/amitadhikary02/ParthaEducare',
      liveUrl: 'https://parthaeducare.com',
      featured: true,
    },
    {
      title: 'Admission Junction',
      description: 'A comprehensive MERN stack platform for college admissions and institutional management. Features college listings, application management, admin dashboard, and seamless UX.',
      image: `${process.env.PUBLIC_URL}/assets/images/Projects/admissionjunction.png`,
      technologies: ['MongoDB', 'Express.js', 'React', 'Node.js'],
      githubUrl: 'https://github.com/amitadhikary02/AdmissionJunction',
      liveUrl: 'https://admissionjunction.com',
      featured: true,
    },
    {
      title: 'Sudoku Solver',
      description: 'An intelligent Sudoku puzzle solver using a backtracking algorithm that efficiently solves puzzles of varying difficulty levels with optimized constraint propagation.',
      image: `${process.env.PUBLIC_URL}/assets/images/Projects/sudoku.png`,
      technologies: ['Python', 'Backtracking', 'Logic', 'CLI'],
      githubUrl: 'https://github.com/amitadhikary02/sudoku_solver',
      liveUrl: '#',
    },
    {
      title: 'Terminal Maze Solver',
      description: 'A sophisticated terminal-based application that solves complex mazes using multiple pathfinding algorithms including BFS, DFS, and A* with visual step-by-step display.',
      image: `${process.env.PUBLIC_URL}/assets/images/Projects/maze.png`,
      technologies: ['Python', 'BFS/DFS/A*', 'Data Structures', 'CLI'],
      githubUrl: 'https://github.com/amitadhikary02/Terminal-Based-Maze-Solver',
      liveUrl: '#',
    },
  ];

  return (
    <section id="projects" className="py-24 lg:py-32 relative overflow-hidden" style={{ background: '#ffffff' }}>
      {/* Background Orbs */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/4 -right-1/4 w-[800px] h-[800px] rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(14,165,233,0.15) 0%, transparent 70%)', filter: 'blur(80px)' }}
        />
        <div
          className="absolute bottom-1/4 -left-1/4 w-[600px] h-[600px] rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(14,165,233,0.1) 0%, transparent 70%)', filter: 'blur(80px)' }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16 lg:mb-24"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: 'rgba(14,165,233,0.1)',
              border: '1px solid rgba(14,165,233,0.2)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
            <span className="text-xs font-medium tracking-wider text-slate-700" style={{ fontFamily: '"Outfit", sans-serif' }}>
              PORTFOLIO
            </span>
          </motion.div>

          <h2
            className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6"
            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', letterSpacing: '-0.03em', color: '#0f172a' }}
          >
            Featured{' '}
            <span className="text-slate-500 italic font-light">Work</span>
          </h2>

          <p className="text-lg sm:text-xl max-w-2xl mx-auto" style={{ color: '#475569', fontFamily: '"Outfit", sans-serif' }}>
            A selection of my recent projects, crafted with precision and passion.
          </p>
        </motion.div>

        {/* Projects grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
          {projects.map((project, index) => (
            <ProjectCard key={project.title} {...project} delay={index * 0.15} />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mt-16 lg:mt-24"
        >
          <motion.a
            href="https://github.com/amitadhikary02"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm font-medium transition-all duration-300 group"
            style={{
              background: 'rgba(14,165,233,0.1)',
              border: '1px solid rgba(14,165,233,0.2)',
              color: '#0f172a',
              fontFamily: '"Outfit", sans-serif',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(14,165,233,0.2)';
              (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.05)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(14,165,233,0.1)';
              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
            }}
          >
            <Github size={18} />
            <span>Explore More on GitHub</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300 opacity-70" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;